var BigNumber	 	= require('../binding/type/BigNumber'),
	BooleanType 	= require('../binding/type/BooleanType'),
	CalendarDate 	= require('../binding/type/CalendarDate'),
	Cell			= require('../data/Cell'),
	Currency 		= require('../binding/type/Currency'),
	Enumeration 	= require('../binding/type/Enumeration'),
	Numeric 		= require('../binding/type/Numeric'),
	Text		 	= require('../binding/type/Text');

/**
 * The Column class represents the field type of columns. It handles
 * styling, data types and data binding.
 *
 * @param {(Object|string)} oData - Data object representing the Column configuration
 * @param {string} oData.property - References the data object property that is bound as value for this column
 * @param {string} [oData.textAlign] - Optional alignment
 * @param {string} [oData.label] - Optional column label that will be shown in the header row
 * @param {string} [oData.type] - Optional data type for this column. Default: Column.Type.Text
 * @param {string} [oData.width] - Optional width of the column
 * @param {SharedStrings} oSharedStrings - SharedStrings instance that will be used by all Cells
 * @param {Styles} oStyles - Styles collection that is needed to register each Style a Column uses
 * @class
 * @module binding/Column
 */
function Column(oData, oSharedStrings, oStyles) {
	var oColumnData;

	/* Fallback assignment if only a key is provided */
	oColumnData = typeof oData === 'string' ? {property: oData} : oData;
	if (!oColumnData || !oColumnData.property) { throw new Error('The property for the column is not defined'); }

	this.property = oColumnData.property;
	this.label = oColumnData.label ? oColumnData.label : oColumnData.property;
	this.width = oColumnData.width;
	this.styles = oStyles;
	this.type = this._initType(oColumnData);
	this.cell = new Cell(this.type, oSharedStrings);
}

/**
 * Initializes the data type for this Column. Type specific
 * formatting information will be evaluated and applied.
 *
 * @param {Object} oData - Configuration object for this Column that also contains type and formatting information
 * @param {string} oData.type - Specifies the data type
 * @param {string} [oData.textAlign] - Defines the horizontal alignment
 * @param {boolean} [oData.delimiter] - Specifies if a thousands delimiter is shown
 * @param {number} [oData.scale] - Specifies the the decimal scale
 * @param {string} [oData.calendar] - Specifies the calendar if type is date, datetime or time
 * @param {string} [oData.format] - Specifies a custom format if type is date, datetime or time
 * @param {string} [oData.unit] - Specifies the UoM if type is number
 * @param {string} [oData.unitProperty] - Name of the property that contains the UoM if type is number or currency
 * @param {boolean} [oData.displayUnit] - Specifies if the UoM will be shown or not if type is number or currency
 * @param {string} [oData.trueValue] - Specifies the displayed value if the boolean type has the value true
 * @param {string} [oData.falseValue] - Specifies the displayed value if the boolean type has the value false
 * @param {string} [oData.template] - Specifies a template string that can be filled with several properties
 * @param {string} [oData.hierarchyLevel] - Specifies the name of the property that contains the hierarchy level
 * @param {string} [oData.inputFormat] - Specifies the input format of a string based date
 * @param {Object} [oData.valueMap] - Contains a JS object or Map with key value pairs
 *
 * @returns {Type} - Returns the specific Type object that contains all the formatting
 * @private
 */
Column.prototype._initType = function(oData) {
	var type, sType;

	sType = typeof oData.type === 'string' ? oData.type.toLowerCase() : oData.type;

	switch (sType) {
		case 'date':
		case 'datetime':
		case 'time':
			type = new CalendarDate(oData);

			/* Add "UTC" to all columns headers of type datetime to indicate that all timestamps in EXCEL are UTC */
			this.label = sType === 'datetime' ? this.label + ' (UTC)' : this.label;
			break;

		case 'number':
			type = new Numeric(oData);
			break;
		case 'currency':
			type = new Currency(oData);
			break;
		case 'boolean':
			type = new BooleanType(oData);
			break;
		case 'enumeration':
			type = new Enumeration(oData);
			break;
		case 'bignumber':
			type = new BigNumber(oData);
			break;
		default:
			oData.type = 'text';

			type = new Text(oData);
	}

	type.registerAt(this.styles);

	return type;
};

/**
 * Returns the Cell object that is referenced by this particular
 * Column. Every Column references only one single Cell that gets
 * reused for each and every Row.
 *
 * When binding the actual data to a row, the Cell content, its
 * references and its styling get updated and a serialized version
 * of the row is stored within the sheet.
 *
 * @returns {Cell} - The Cell that belongs to this particular Column
 */
Column.prototype.getCell = function() {
	return this.cell;
};

/**
 * This method binds the actual business object property to the columns cell.
 *
 * @param {object} oData - Business object that gets bound to the Cell of this Column.
 */
Column.prototype.bind = function(oData) {

	/*
	 * Due to the fact that Cell#setValue updates the Style depending
	 * on the actual Cell value (i.E. Unit of Measurement for
	 * Currencies), we need to provide the original business object
	 * so that the Cell knows the whole business object instead of
	 * just the value of its particular property.
	 */
	this.cell.setValue(this.property, oData);
};

module.exports = Column;


//////////////////
// WEBPACK FOOTER
// ./core/xlsx/src/binding/Column.js
// module id = 25
// module chunks = 0